import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Map, List } from 'immutable';
import { connect } from 'react-redux';
import classNames from 'classnames';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import { getChanged, isDeepChanged, getIn, unwrap } from '../../utils/ChangeSpy';
import getOptionValue from '../../utils/getOptionValue';
import { showError } from '../../utils/MessagePopup';
import {
  findUsers,
  changeField,
  findIndustries,
  clearSuggest,
  makeContactExecutive,
  findAnalysts,
  findDirectors,
} from '../../actions/contact/contactExecutive';
import { changeContactInfoField, closeValidationError, setNameFieldError } from '../../actions/contactDetail';
import config from '../../config';
import AutoComplete from '../AutoComplete';
import { checkNameChangable, checkFirstName, checkLastName, parseFullName } from '../services/nameRules';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { useRedirectForceContext } from '../../contexts/RedirectForceContext';
import { usePopupContext } from '../../contexts/PopupContext';

const emptyMap = new Map();

// Static handler
const handleGetValue = suggestion => suggestion?.value ?? '';

const AddExecutiveContactPopupContainer = ({
  children,
  contact,
  contactInfo,
  executive,
  models,
  tags,
  inputErrors,
  user,
  popup,
  closeValidationError,
  changeField,
  changeContactInfoField,
  findUsers,
  findIndustries,
  findAnalysts,
  findDirectors,
  makeContactExecutive,
  setNameFieldError,
}) => {
  const redirectForce = useRedirectForceContext();
  const { openPopup } = usePopupContext();
  const [submitting, setSubmitting] = useState(false);
  const changableRef = useRef({ fullName: false, firstName: false, lastName: false });
  const contactRef = useRef(contact);

  contactRef.current = contact;

  useEffect(() => {
    if (user) {
      changeField({ name: 'suggestDirector', value: user.get('module') || '' });
      changeField({ name: 'recordOwnerId', value: user.get('moduleId') || 0 });

      const roles = user.get('roles', List());

      if (roles.size && roles.getIn([0, 'slug']) === config.ANALYST_ASSOCIATE) {
        changeField({ name: 'suggestAnalyst', value: user.get('userName') });
        changeField({ name: 'recordSubOwnerId', value: user.get('id') });
      }
    }
  }, []);

  const handleErrorClose = useCallback((event, field) => {
    closeValidationError({ field });
  }, []);

  const trimDoubleSpaces = useCallback(name => {
    const nameValue = name || '';

    return nameValue.replace(/\s{2,}/g, ' ');
  }, []);

  const updateFirstName = useCallback(
    name => {
      const contact = contactRef.current;
      const trimmedName = trimDoubleSpaces(name);
      const firstName = unwrap(contact.get('firstName') || '');
      let fullName = unwrap(contact.get('fullName') || '');

      fullName = fullName.indexOf(firstName) > -1 ? fullName.substring(firstName.length) : fullName;

      if (changableRef.current.firstName) {
        changeContactInfoField({ name: 'firstName', value: trimmedName });
      }

      if (changableRef.current.fullName) {
        changeContactInfoField({ name: 'fullName', value: trimDoubleSpaces(`${trimmedName} ${fullName}`) });
      }
    },
    [contact, changeContactInfoField, trimDoubleSpaces],
  );

  const updateLastName = useCallback(
    name => {
      const contact = contactRef.current;
      const trimmedName = trimDoubleSpaces(name);
      const lastName = unwrap(contact.get('lastName') || '');
      let fullName = unwrap(contact.get('fullName') || '');

      fullName = fullName.lastIndexOf(lastName) > -1 ? fullName.substring(0, fullName.lastIndexOf(lastName)) : fullName;

      if (changableRef.current.lastName) {
        changeContactInfoField({ name: 'lastName', value: trimmedName });
      }

      if (changableRef.current.fullName) {
        changeContactInfoField({ name: 'fullName', value: trimDoubleSpaces(`${fullName} ${trimmedName}`) });
      }
    },
    [contact, changeContactInfoField, trimDoubleSpaces],
  );

  const updateFullName = useCallback(
    name => {
      const nameParts = parseFullName(name);

      if (changableRef.current.firstName) {
        changeContactInfoField({ name: 'firstName', value: nameParts[0] });
      }

      if (changableRef.current.lastName) {
        changeContactInfoField({ name: 'lastName', value: nameParts[1] });
      }

      if (changableRef.current.fullName) {
        changeContactInfoField({ name: 'fullName', value: nameParts[2] });
      }
    },
    [changeContactInfoField],
  );

  const handleChangeExecutive = useCallback(
    ({ target: { name, value } }) => {
      switch (name) {
        case 'firstName':
          updateFirstName(value);

          return;

        case 'lastName':
          updateLastName(value);

          return;

        case 'fullName':
          updateFullName(value);

          return;

        default:
          return changeContactInfoField({ name, value });
      }
    },
    [updateFirstName, updateLastName, updateFullName, changeContactInfoField],
  );

  const handleChangeInfo = useCallback(
    ({ target: { name, value } }) => {
      switch (name) {
        case 'firstName':
          updateFirstName(value);

          return;

        case 'lastName':
          updateLastName(value);

          return;

        case 'fullName':
          updateFullName(value);

          return;

        default:
          return changeContactInfoField({ name, value });
      }
    },
    [updateFirstName, updateLastName, updateFullName, changeContactInfoField],
  );

  const handleCreate = useCallback(() => {
    if (contact.get('isValid')) {
      const id = contact.get('id') || 'new';
      const body = getChanged(contact);
      const execFields = getChanged(executive);
      const prefix = getOptionValue(contact.get('prefix'));
      const suffix = getOptionValue(contact.get('suffix'));

      const afterError = ({ responseBody }) => {
        setSubmitting(false);

        if (responseBody) {
          showError(openPopup, [responseBody.message], null, () => openPopup('AddExecutiveContactPopup'));
        }
      };

      const afterSuccess = ({ response }) => {
        setTimeout(() => {
          redirectForce(`/contact/${response.id}/executive`);
        }, 0);
      };

      setSubmitting(true);

      const industryCategoryIds = [
        ...models.reduce((accumulator, model) => {
          if (model.get('checked')) {
            accumulator.push(model.get('id'));
          }

          return accumulator;
        }, []),
        ...tags.reduce((accumulator, tag) => {
          if (tag.get('checked')) {
            accumulator.push(tag.get('id'));
          }
          tag.get('children', List()).forEach(child => {
            if (child.get('checked')) {
              accumulator.push(child.get('id'));
            }
          });

          return accumulator;
        }, []),
      ];

      makeContactExecutive(
        {
          ...body,
          ...execFields,
          prefix,
          suffix,
          id,
          industryCategoryIds,
        },
        afterSuccess,
        afterError,
      );
    }
  }, [contact, executive, tags, models, openPopup, redirectForce, makeContactExecutive]);

  const handleBlur = useCallback(
    ({ name, value }) => {
      const MAX_LENGTH = 50;

      if (name === 'firstName' && !checkFirstName(value)) {
        setNameFieldError({ field: 'firstName', value: 'Enter only the first name with no initials, etc.' });
      } else if (name === 'firstName' && value.length >= MAX_LENGTH) {
        setNameFieldError({
          field: 'firstName',
          value: `The first name may not be greater than ${MAX_LENGTH} characters.`,
        });
      } else if (name === 'lastName' && !checkLastName(value)) {
        setNameFieldError({
          field: 'lastName',
          value: 'Enter only the last name with no initials, suffixes, etc.',
        });
      } else if (name === 'lastName' && value.length >= MAX_LENGTH) {
        setNameFieldError({
          field: 'lastName',
          value: `The last name may not be greater than ${MAX_LENGTH} characters.`,
        });
      } else if (name === 'nick' && value.length >= MAX_LENGTH) {
        setNameFieldError({ field: 'nick', value: `The nick may not be greater than ${MAX_LENGTH} characters.` });
      } else if (name === 'fullName' && value.length >= 100) {
        setNameFieldError({ field: 'fullName', value: 'The full name may not be greater than 100 characters.' });
      }
    },
    [setNameFieldError],
  );

  const handleFocus = useCallback(({ target: { name } }) => {
    if (['firstName', 'lastName', 'fullName'].indexOf(name) > -1) {
      changableRef.current = checkNameChangable(name);
    }
  }, []);

  const handleBICClick = useCallback(() => {
    openPopup('TagsManagementPopup', {
      addNew: true,
      clientView: true,
      cb: () =>
        openPopup('AddExecutiveContactPopup', {
          fromPositionPopup: popup && popup.getIn(['props', 'fromPositionPopup']),
          cb: popup && popup.getIn(['props', 'cb']),
        }),
    });
  }, [openPopup, popup]);

  const handleFindEmail = useCallback(() => {
    openPopup('EmailFinderPopup', {
      contact,
      executive,
      models,
      tags,
      inputErrors,
      user,
      reopenPopupName: 'AddExecutiveContactPopup',
    });
  }, [openPopup, contact, executive, models, tags, inputErrors, user]);

  const handleFetchIndustries = useCallback(
    ({ value }) => {
      findIndustries({ filter: value });
    },
    [findIndustries],
  );

  const handleUpdateSuggestions = useCallback(
    (suggestionName, param) => {
      if ('text' in param) {
        changeField({ name: suggestionName, value: param.text });
      }

      if ('suggestions' in param) {
        changeField({ name: 'suggests', value: param.suggestions });
      }
    },
    [changeField],
  );

  const handleUpdateIndustry = useCallback(param => handleUpdateSuggestions('suggestIndustry', param), [
    handleUpdateSuggestions,
  ]);

  const handleUpdateDirector = useCallback(param => handleUpdateSuggestions('suggestDirector', param), [
    handleUpdateSuggestions,
  ]);

  const handleUpdateAnalyst = useCallback(param => handleUpdateSuggestions('suggestAnalyst', param), [
    handleUpdateSuggestions,
  ]);

  const handleSelectUser = useCallback(
    (name, event, { suggestion }) => {
      changeField({ name, value: suggestion.text });
    },
    [changeField],
  );

  const handleSelectDirector = useCallback((event, data) => handleSelectUser('suggestDirector', event, data), [
    handleSelectUser,
  ]);

  const handleSelectAnalyst = useCallback((event, data) => handleSelectUser('suggestAnalyst', event, data), [handleSelectUser]);

  const handleSelectIndustry = useCallback(
    (event, { suggestion }) => {
      changeField({ name: 'suggestIndustry', value: suggestion.text });
      changeField({ name: 'industryId', value: suggestion.id });
    },
    [changeField],
  );

  const handleChangeSuggestion = useCallback(
    object => {
      if (typeof object.value === 'string') {
        const { value } = object;
        const nextValue = contactInfo
          .get('suffixes')
          .find(suffix => suffix.get('name') === value, null, Map({ name: value || '', value: value || '' }));

        changeContactInfoField({ name: 'suffix', value: nextValue });
      }
    },
    [contactInfo, changeContactInfoField],
  );

  const getSuggestProps = useCallback(
    name => {
      let onFetch = findUsers;

      if (name === 'Industry') {
        onFetch = handleFetchIndustries;
      } else if (name === 'Director') {
        onFetch = ({ value }) => findDirectors({ filter: value });
      } else if (name === 'Analyst') {
        onFetch = ({ value }) => findAnalysts({ filter: value });
      }

      const onUpdateMap = {
        Industry: handleUpdateIndustry,
        Director: handleUpdateDirector,
        Analyst: handleUpdateAnalyst,
      };

      const onSelectMap = {
        Industry: handleSelectIndustry,
        Director: handleSelectDirector,
        Analyst: handleSelectAnalyst,
      };

      return {
        onFetch,
        onUpdate: onUpdateMap[name],
        getValue: handleGetValue,
        onSelect: onSelectMap[name],
        value: executive.get(`suggest${name}`, ''),
      };
    },
    [
      executive,
      findUsers,
      findDirectors,
      findAnalysts,
      handleFetchIndustries,
      handleUpdateIndustry,
      handleUpdateDirector,
      handleUpdateAnalyst,
      handleSelectIndustry,
      handleSelectDirector,
      handleSelectAnalyst,
    ],
  );

  const getSuffixesSuggest = useCallback(() => {
    const value = getIn(contactInfo, 'suffix').get('name');
    const idValue = value;
    const regExp = new RegExp(String(value).replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1'), 'i');
    const className = classNames('form-control input-sm', {
      changed: isDeepChanged(contactInfo.get('suffix')),
    });
    const suggestions = getIn(contactInfo, 'suffixes')
      .filter(suffix => regExp.test(suffix.get('name')))
      .map(suffix => ({ id: suffix.get('value'), text: suffix.get('name') }));

    return (
      <AutoComplete
        idValue={idValue}
        inputProps={{ className }}
        loading={false}
        name="suffix"
        onChange={handleChangeSuggestion}
        onFind={args => {
          setTimeout(
            () =>
              args.afterSuccess({
                response: {
                  data: getIn(contactInfo, 'suffixes')
                    .filter(suffix => regExp.test(suffix.get('name')))
                    .map(suffix => ({ id: suffix.get('value'), text: suffix.get('name') })),
                },
              }),
            0,
          );
        }}
        suggestions={suggestions}
        suggestionsName="suffixes"
        value={value}
        suggestIfEmpty
      />
    );
  }, [contactInfo, handleChangeSuggestion]);

  const suggestDirector = getSuggestProps('Director');
  const suggestAnalyst = getSuggestProps('Analyst');
  const suggestIndustry = getSuggestProps('Industry');
  const selectedTags = tags.filter(tag => tag.get('checked'));
  const selectedModels = models.filter(model => model.get('checked'));
  const isValid = inputErrors && inputErrors.size === 0 && selectedTags.size > 0 && selectedModels.size > 0;

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
      <>
        <Popups.AddExecutiveContactPopup
          contact={contact}
          executive={executive}
          isValid={isValid}
          models={models}
          onBICClick={handleBICClick}
          onBlur={handleBlur}
          onChangeExecutive={handleChangeExecutive}
          onChangeInfo={handleChangeInfo}
          onCreate={handleCreate}
          onFindEmail={handleFindEmail}
          onFocus={handleFocus}
          prefix="newContact"
          submitting={submitting}
          suggestAnalyst={suggestAnalyst}
          suggestDirector={suggestDirector}
          suggestIndustry={suggestIndustry}
          suggestSuffix={getSuffixesSuggest()}
          tags={tags}
        />
        {children}
      </>
    </PrettyErrorProvider>
  );
};

function mapStateToProps(state, props) {
  return {
    ...props,
    inputErrors: state.contact.info
      .get('inputErrors', emptyMap)
      .merge(state.contact.executive.get('inputErrors', emptyMap)),
    contact: state.contact.info,
    contactInfo: state.contact.info,
    executive: state.contact.executive,
    models: getIn(state.businessModels, 'models', List()),
    tags: getIn(state.industryTags, 'tags', List()),
    user: state.auth.get('user', null),
  };
}

export { AddExecutiveContactPopupContainer };

const Connected = connect(
  mapStateToProps,
  {
    findUsers,
    closeValidationError,
    changeContactInfoField,
    findIndustries,
    clearSuggest,
    makeContactExecutive,
    changeField,
    findAnalysts,
    findDirectors,
    setNameFieldError,
  },
  mergeProps,
  connectOptions,
)(memo(AddExecutiveContactPopupContainer));

Connected.closable = false;

export default Connected;
